/**
 * Phase 101 W3 — Pack API client.
 *
 * Provides React Query hooks covering all Pack CRUD.
 * Canonical backend mount: /api/packs (D-08 locked).
 *
 * Hooks:
 *   usePacksByLead(leadId)            — GET /api/packs?leadId=X
 *   useCreatePack()                   — POST /api/packs
 *   useRebindInvoiceToPack()          — PATCH /api/invoices/:id/rebind-to-pack
 *   useUpdatePack()                   — PATCH /api/packs/:id
 *   useBanPackAccount()               — PATCH /api/packs/:id/ban-account
 *   useSetPackAccountMonitoring()     — PATCH /api/packs/:id/accounts/:accountId  (pack-editing B)
 *   useDeletePackAccount()            — DELETE /api/packs/:id/accounts/:accountId
 *   useAddPackAccount()               — POST /api/packs/:id/accounts
 *   useAddPackAccountById()           — PATCH /api/packs/:id/add-account  (F5 move/add-existing)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client.js';
import { queryKeys } from '../lib/queryKeys.js';

// ── Queries ─────────────────────────────────────────────────────────────────

/**
 * GET /api/packs?leadId=X
 * Returns list of Pack objects with accounts + representativeAccount.
 */
export function usePacksByLead (leadId) {
  return useQuery({
    queryKey: queryKeys.packs.byLead(leadId),
    queryFn: () => fetchPacksEnvelope(leadId),
    select: (data) => data?.packs ?? [],
    enabled: !!leadId,
    staleTime: 30_000,
  });
}

// Shared fetch: the envelope also carries leadMonitoringPaused (blogger-wide
// monitoring pause). Both hooks share one queryKey → one request.
async function fetchPacksEnvelope (leadId) {
  const { data } = await apiClient.get('/packs', { params: { leadId } });
  return data ?? {};
}

/**
 * SCOPE-PARSE-TOGGLES 2026-09-17 — optimistic write on the shared GET /packs envelope.
 *
 * ONE queryKey feeds usePacksByLead AND useLeadMonitoringPaused, so a single
 * setQueryData moves every parsing switch on the blogger card at once and one rollback
 * restores the whole picture. Pattern: cancel in-flight refetches → snapshot → patch;
 * the caller's `onError` hands the snapshot back to `rollbackPacksEnvelope`, and the
 * hook's own `onSuccess` invalidation is what replaces the guess with the server's
 * answer. A cache that holds nothing yet (first paint) returns `null`: there is
 * nothing to patch and nothing to roll back.
 */
export async function optimisticPacksEnvelope (qc, leadId, patch) {
  if (!leadId) return null;
  const queryKey = queryKeys.packs.byLead(leadId);
  await qc.cancelQueries({ queryKey });
  const prev = qc.getQueryData(queryKey);
  if (prev === undefined) return null;
  qc.setQueryData(queryKey, patch(prev));
  return { queryKey, prev };
}

/** Undo an `optimisticPacksEnvelope` write (its return value is the context). */
export function rollbackPacksEnvelope (qc, ctx) {
  if (ctx) qc.setQueryData(ctx.queryKey, ctx.prev);
}

/** Blogger-wide monitoring pause flag from the same GET /packs envelope. */
export function useLeadMonitoringPaused (leadId) {
  return useQuery({
    queryKey: queryKeys.packs.byLead(leadId),
    queryFn: () => fetchPacksEnvelope(leadId),
    select: (data) => data?.leadMonitoringPaused ?? false,
    enabled: !!leadId,
    staleTime: 30_000,
  });
}

/**
 * PATCH /api/monitoring-control/leads/:leadId/monitoring-pause { paused }.
 * Blogger-wide kill switch: the parser's eligibility sync turns OFF every
 * bridged account of the lead (all packs, all amb accounts) while paused.
 */
export function useSetLeadMonitoringPause () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, paused }) => {
      const { data } = await apiClient.patch(`/monitoring-control/leads/${leadId}/monitoring-pause`, { paused });
      return data;
    },
    onMutate: ({ leadId, paused } = {}) => optimisticPacksEnvelope(
      qc, leadId, (data) => ({ ...data, leadMonitoringPaused: paused }),
    ),
    onError: (_err, _variables, ctx) => rollbackPacksEnvelope(qc, ctx),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables?.leadId) });
    },
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

/**
 * POST /api/packs
 * Create a new pack for a lead. Auto-label «Пачка N» on server.
 * onSuccess: invalidate packs.byLead(variables.leadId) + packs.all
 */
export function useCreatePack () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, label, type, application, accountIds, representativeAccountId, newAccounts } = {}) => {
      const { data } = await apiClient.post('/packs', {
        leadId,
        label,
        application, // PACK-APP-REQ: required by the server (400 without it), every create UI picks it
        // PACK-CREATE-UI §3.3 — the Инфо dialog's type select, sent in the SAME request
        // as the accounts (a POST-then-PATCH pair is not atomic). The four money flows
        // pass no type, so the conditional spread keeps their body byte-identical and
        // the server falls back to the column default `test`.
        ...(type ? { type } : {}),
        ...(Array.isArray(accountIds) && accountIds.length > 0 ? { accountIds } : {}),
        // newAccounts ([{platform, handle, profileUrl?}]) are inserted server-side as
        // accountPurpose:'ambassador' (packs.js POST). Previously dropped here → "пачка
        // создана" but the manually-entered ambassador accounts never persisted.
        ...(Array.isArray(newAccounts) && newAccounts.length > 0 ? { newAccounts } : {}),
        ...(representativeAccountId ? { representativeAccountId } : {}),
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables?.leadId) });
      qc.invalidateQueries({ queryKey: queryKeys.packs.all });
      // Account list cache also needs invalidation — newly attached accounts now
      // carry packId, downstream views (TestingView pack section, AccountCard chips)
      // must re-fetch.
      qc.invalidateQueries({ queryKey: ['accounts', variables?.leadId] });
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.accounts.detail(variables.leadId) });
      }
      // Creating a pack changes the active-pack count shown in the Bloggers list
      // «Пачки» column (M2, sourced from GET /leads). Refresh the lead lists.
      qc.invalidateQueries({ queryKey: queryKeys.leads.all });
    },
  });
}

/**
 * PATCH /api/invoices/:invoiceId/rebind-to-pack { packId }
 * Rebind a legacy invoice to a pack. D-04a contract gate enforced on server.
 * onSuccess: invalidate invoices.all + packs.all + optional byContract
 */
export function useRebindInvoiceToPack () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ invoiceId, packId, contractId } = {}) => {
      const { data } = await apiClient.patch(`/invoices/${invoiceId}/rebind-to-pack`, { packId });
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all() });
      qc.invalidateQueries({ queryKey: queryKeys.packs.all });
      if (variables?.contractId) {
        qc.invalidateQueries({ queryKey: queryKeys.invoices.byContract(variables.contractId) });
      }
    },
    // onError no-op — callers provide their own error toast handling
  });
}

/**
 * PATCH /api/packs/:id
 * Update pack fields through the scoped global writer, including type/application.
 * Refresh pack, monitoring report/list and monthly caches after success.
 */
export function useUpdatePack () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, leadId, ...fields } = {}) => {
      const { data } = await apiClient.patch(`/packs/${id}`, fields);
      return data;
    },
    onSuccess: (_data, variables) => {
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables.leadId) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.packs.all });
      qc.invalidateQueries({ queryKey: ['monitoring2'] });
      if (variables?.id) qc.invalidateQueries({ queryKey: ['rounds', 'monthly', variables.id] });
    },
  });
}

/**
 * DELETE /api/packs/:id — delete an EMPTY pack. 409 pack_has_accounts|rounds|invoices otherwise.
 */
export function useDeletePack () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id } = {}) => {
      const { data } = await apiClient.delete(`/packs/${id}`);
      return data;
    },
    onSuccess: (_data, variables) => {
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables.leadId) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.packs.all });
      qc.invalidateQueries({ queryKey: ['monitoring2'] });
    },
  });
}

/**
 * PATCH /api/packs/:id/ban-account
 * Atomic TX: ban + checkpoint + rep rotation + optional replacement.
 * onSuccess: invalidate packs.byLead + roundProgress
 */
export function useBanPackAccount () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, leadId, trackedAccountId, replacement } = {}) => {
      const { data } = await apiClient.patch(`/packs/${id}/ban-account`, { trackedAccountId, replacement });
      return data;
    },
    onSuccess: (_data, variables) => {
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables.leadId) });
      }
      if (variables?.id) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.roundProgress(variables.id) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.packs.all });
    },
  });
}

/**
 * PATCH /api/packs/:id/accounts/:accountId { enabled } — выключить/включить
 * аккаунт пачки (pack-editing B). Выключенный выпадает из парсера и из
 * математики открытого раунда; история не трогается.
 */
export function useSetPackAccountMonitoring () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, accountId, enabled } = {}) => {
      const { data } = await apiClient.patch(`/packs/${id}/accounts/${accountId}`, { enabled });
      return data;
    },
    onMutate: ({ id, accountId, leadId, enabled } = {}) => optimisticPacksEnvelope(
      qc, leadId, (data) => ({
        ...data,
        packs: (data.packs ?? []).map((p) => (p.id !== id ? p : {
          ...p,
          accounts: (p.accounts ?? []).map((a) => (a.id !== accountId ? a : { ...a, monitoringEnabled: enabled })),
        })),
      }),
    ),
    onError: (_err, _variables, ctx) => rollbackPacksEnvelope(qc, ctx),
    onSuccess: (_data, variables) => {
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables.leadId) });
      }
      if (variables?.id) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.roundProgress(variables.id) });
        qc.invalidateQueries({ queryKey: queryKeys.rounds.byPack(variables.id) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.rounds.overviewAll });
    },
  });
}

/**
 * DELETE /api/packs/:id/accounts/:accountId — полное удаление мусорного
 * аккаунта (pack-editing B). 409 account_has_posts, если у акка есть посты.
 */
export function useDeletePackAccount () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, accountId } = {}) => {
      const { data } = await apiClient.delete(`/packs/${id}/accounts/${accountId}`);
      return data;
    },
    onSuccess: (_data, variables) => {
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables.leadId) });
        qc.invalidateQueries({ queryKey: queryKeys.accounts.detail(variables.leadId) });
      }
      if (variables?.id) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.roundProgress(variables.id) });
        qc.invalidateQueries({ queryKey: queryKeys.rounds.byPack(variables.id) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.rounds.overviewAll });
    },
  });
}

/**
 * POST /api/packs/:id/accounts { platform, handle } — добавить аккаунт в пачку
 * (pack-editing B «+ Аккаунт»). Дедуп на сервере.
 */
export function useAddPackAccount () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, platform, handle, profileUrl } = {}) => {
      const { data } = await apiClient.post(`/packs/${id}/accounts`, { platform, handle, profileUrl });
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.packs.all }); // ['packs'] — covers byLead + roundProgress regardless of leadId
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables.leadId) });
        qc.invalidateQueries({ queryKey: queryKeys.accounts.detail(variables.leadId) });
      }
      if (variables?.id) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.roundProgress(variables.id) });
        qc.invalidateQueries({ queryKey: queryKeys.rounds.byPack(variables.id) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.rounds.overviewAll });
    },
  });
}

/**
 * PATCH /api/packs/:id/add-account { trackedAccountId } — переместить/добавить
 * СУЩЕСТВУЮЩИЙ аккаунт лида в пачку (F5). Бэкенд двигает packId и чинит
 * representative обеих пачек (services/packMove.js).
 */
export function useAddPackAccountById () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, trackedAccountId } = {}) => {
      const { data } = await apiClient.patch(`/packs/${id}/add-account`, { trackedAccountId });
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.packs.all });
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables.leadId) });
        qc.invalidateQueries({ queryKey: queryKeys.accounts.detail(variables.leadId) });
      }
      if (variables?.id) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.roundProgress(variables.id) });
        qc.invalidateQueries({ queryKey: queryKeys.rounds.byPack(variables.id) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.rounds.overviewAll });
    },
  });
}
